import React, { useState, useMemo, useCallback } from 'react';
import {
  Table, Tag, Space, Button, Card, Form, Input, Select,
  Typography, Modal, Row, Col, Statistic, message, Tooltip
} from 'antd';
import {
  SearchOutlined, ReloadOutlined,
  ClockCircleOutlined, SyncOutlined, CheckCircleOutlined,
  MinusCircleOutlined, AlertOutlined, PlusOutlined,
  PaperClipOutlined,
} from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';

const { Title, Text } = Typography;
const { Option } = Select;

// ══════════════════════════════════════════════
//  常量映射
// ══════════════════════════════════════════════

const STATUS_MAP = {
  pending:    { label: '待处理',   color: 'blue',    icon: <ClockCircleOutlined /> },
  processing: { label: '处理中',   color: 'orange',  icon: <SyncOutlined spin /> },
  resolved:   { label: '已解决',   color: 'green',   icon: <CheckCircleOutlined /> },
  closed:     { label: '已关闭',   color: 'default', icon: <MinusCircleOutlined /> },
};

const PRIORITY_MAP = {
  low:      { label: '低',   color: 'default' },
  normal:   { label: '普通', color: 'blue' },
  high:     { label: '高',   color: 'orange' },
  urgent:   { label: '紧急', color: 'red' },
};

const CATEGORY_MAP = {
  techSupport: { label: '技术对接', color: 'purple' },
  bug:         { label: 'BUG',      color: 'red' },
  requirement: { label: '需求',     color: 'cyan' },
  other:       { label: '其他',     color: 'default' },
};

// TAPD 单状态映射（tapd.status）
const TAPD_STATUS_MAP = {
  developing: { label: '开发中', color: 'blue' },
  testing:    { label: '测试中', color: 'purple' },
  closed:     { label: '已关闭', color: 'default' },
};

// 问题类型（当前已知枚举）
const ISSUE_TYPE_OPTIONS = [
  'PAAS平台', 'SAAS平台', '生态平台', '安卓app', 'IOS APP',
  '固件问题', '固件SDK', '安卓SDK', 'IOS SDK', '设置问题',
];

// 所属App展示格式：App应用名称-系统-区域
// TODO: 联调时替换为接口数据
const APP_DISPLAY_MAP = {
  'app_qx-iOS-GL': '牵心Pro-iOS-全球',
  'app_qx-Android-GL': '牵心Pro-Android-全球',
  'app_hs-iOS-CN': 'HomeStar-iOS-国内',
  'app_hs-Android-CN': 'HomeStar-Android-国内',
  'app_hs-iOS-OV': 'HomeStar-iOS-海外',
};

// ══════════════════════════════════════════════
//  Mock 数据
//  TODO: 联调时替换为接口数据
// ══════════════════════════════════════════════

const MOCK_TICKETS = [
  { key: '1', ticketNo: 'TK202607270001', title: 'test-SDK初始化接口返回错误码1001',
    enterpriseId: 'test-ent-001', enterpriseName: 'test-深圳智创科技有限公司',
    category: 'techSupport', issueType: 'IOS SDK', priority: 'urgent', status: 'pending',
    appValue: 'app_qx-iOS-GL', projectName: 'SDK联调项目',
    handlerName: null, handlerId: null, handlerAccount: null,
    description: '调用SDK初始化接口时返回错误码1001，环境为iOS 18，SDK版本v3.1.0',
    attachments: [
      { name: '错误日志.txt', type: 'txt', size: '86KB', url: '#' },
      { name: 'SDK版本信息.png', type: 'image', size: '256KB', url: 'https://picsum.photos/seed/hm-sdk/400/300' },
    ],
    tapd: null, // 未创建 TAPD 单
    createdAt: '2026-07-27 09:15:00', updatedAt: '2026-07-27 09:15:00',
  },
  { key: '2', ticketNo: 'TK202607270002', title: 'test-License采购量统计对不上',
    enterpriseId: 'test-ent-002', enterpriseName: 'test-广州迅捷科技有限公司',
    category: 'techSupport', issueType: 'SAAS平台', priority: 'high', status: 'processing',
    appValue: 'app_hs-iOS-CN', projectName: '私有化部署',
    handlerName: '李客服', handlerId: 'user-01', handlerAccount: 'lifuwu',
    description: '后台显示的License采购数量与实际下单数量不一致，少了20个',
    attachments: [
      { name: '采购订单截图.png', type: 'image', size: '1.2MB', url: 'https://picsum.photos/seed/hm-order/400/300' },
    ],
    tapd: { workspaceId: '32860459', workspaceName: '鹤梦云-IoT平台研发空间（32860459）', type: 'bug', tapdId: '1139258384001035530', title: 'test-License采购量统计对不上', status: 'developing' },
    createdAt: '2026-07-27 08:30:00', updatedAt: '2026-07-27 10:00:00',
  },
  { key: '3', ticketNo: 'TK202607270003', title: 'test-设备固件OTA升级失败',
    enterpriseId: 'test-ent-003', enterpriseName: 'test-上海云联科技有限公司',
    category: 'bug', issueType: '固件问题', priority: 'high', status: 'pending',
    appValue: '', projectName: '',
    handlerName: null, handlerId: null, handlerAccount: null,
    description: '设备型号X200，固件版本v2.3→v2.5升级过程中设备重启后变砖',
    attachments: [
      { name: '设备变砖现场照片.png', type: 'image', size: '2.4MB', url: 'https://picsum.photos/seed/hm-brick/400/300' },
      { name: 'OTA升级日志.zip', type: 'zip', size: '5.8MB', url: '#' },
    ],
    createdAt: '2026-07-27 07:45:00', updatedAt: '2026-07-27 07:45:00',
  },
  { key: '4', ticketNo: 'TK202607260004', title: 'test-App OEM定制需要新增品牌配置页',
    enterpriseId: 'test-ent-001', enterpriseName: 'test-深圳智创科技有限公司',
    category: 'requirement', issueType: '安卓app', priority: 'normal', status: 'resolved',
    appValue: 'app_qx-Android-GL', projectName: 'SDK联调项目',
    handlerName: '王客服', handlerId: 'user-02', handlerAccount: 'wangyuan',
    description: '希望在App OEM定制中增加一个品牌配置页面，用于设置Logo和主题色',
    attachments: [
      { name: '品牌配置页需求原型.zip', type: 'zip', size: '12.6MB', url: '#' },
    ],
    tapd: { workspaceId: '32860459', workspaceName: '鹤梦云-IoT平台研发空间（32860459）', type: 'story', tapdId: '1132860459001101556', title: 'test-App OEM定制需要新增品牌配置页', status: 'testing' },
    createdAt: '2026-07-26 16:00:00', updatedAt: '2026-07-27 11:00:00',
  },
  { key: '5', ticketNo: 'TK202607250005', title: 'test-云平台私有化部署环境确认',
    enterpriseId: 'test-ent-005', enterpriseName: 'test-北京数联科技有限公司',
    category: 'techSupport', issueType: 'PAAS平台', priority: 'normal', status: 'closed',
    appValue: '', projectName: '私有化部署',
    handlerName: '张客服', handlerId: 'user-03', handlerAccount: 'zhangwei',
    description: '确认私有化部署的服务器配置和环境要求',
    attachments: [
      { name: '服务器配置清单.xlsx', type: 'xlsx', size: '48KB', url: '#' },
    ],
    tapd: { workspaceId: '39258384', workspaceName: '鹤梦云-APP研发空间（39258384）', type: 'story', tapdId: '1132860459001101024', title: 'test-云平台私有化部署环境确认', status: 'closed' },
    createdAt: '2026-07-25 14:20:00', updatedAt: '2026-07-26 09:00:00',
  },
  { key: '6', ticketNo: 'TK202607270006', title: 'test-平台API对接认证失败',
    enterpriseId: 'test-ent-004', enterpriseName: 'test-成都天视科技有限公司',
    category: 'techSupport', issueType: '生态平台', priority: 'normal', status: 'pending',
    appValue: 'app_hs-iOS-OV', projectName: '',
    handlerName: null, handlerId: null, handlerAccount: null,
    description: '调用平台API时token认证一直返回401',
    attachments: [
      { name: 'API请求日志.txt', type: 'txt', size: '32KB', url: '#' },
      { name: '认证失败截图.png', type: 'image', size: '380KB', url: 'https://picsum.photos/seed/hm-auth/400/300' },
    ],
    createdAt: '2026-07-27 10:30:00', updatedAt: '2026-07-27 10:30:00',
  },
  { key: '7', ticketNo: 'TK202607270007', title: 'test-批量设备管理需求',
    enterpriseId: 'test-ent-006', enterpriseName: 'test-杭州联智科技有限公司',
    category: 'requirement', issueType: '', priority: 'low', status: 'processing',
    appValue: 'app_hs-Android-CN', projectName: '已归档项目',
    handlerName: '李客服', handlerId: 'user-01', handlerAccount: 'lifuwu',
    description: '需要支持一次性导入1000+设备的批量管理功能',
    attachments: [
      { name: '批量设备管理需求说明.pdf', type: 'pdf', size: '860KB', url: '#' },
    ],
    tapd: null, // 已领取但尚未创建 TAPD 单（演示创建流程）
    createdAt: '2026-07-27 11:00:00', updatedAt: '2026-07-27 13:00:00',
  },
  { key: '8', ticketNo: 'TK202607240008', title: 'test-套餐服务配置咨询',
    enterpriseId: 'test-ent-002', enterpriseName: 'test-广州迅捷科技有限公司',
    category: 'other', issueType: '设置问题', priority: 'low', status: 'resolved',
    appValue: '', projectName: '',
    handlerName: '王客服', handlerId: 'user-02', handlerAccount: 'wangyuan',
    description: '咨询如何配置设备的增值服务套餐',
    attachments: [],
    createdAt: '2026-07-24 13:00:00', updatedAt: '2026-07-25 16:00:00',
  },
];

// =============== 索引：快速定位已登录客服 ===============
const CURRENT_AGENT = { id: 'user-99', name: '当前客服', account: 'dangqian' };

// =============== Mock 客服账号（供分配选择，与 AgentManage 同源） ===============
// TODO: 联调时替换为接口数据
const MOCK_AGENTS = [
  { id: 'user-01', name: '李客服', account: 'lifuwu' },
  { id: 'user-02', name: '王客服', account: 'wangyuan' },
  { id: 'user-03', name: '张客服', account: 'zhangwei' },
  { id: 'user-04', name: '赵客服', account: 'zhaoyun' },
  { id: 'user-05', name: '陈客服', account: 'chenli' },
];

// ══════════════════════════════════════════════
//  排序辅助函数
// ══════════════════════════════════════════════

const STATUS_ORDER = { pending: 0, processing: 1, resolved: 2, closed: 3 };
const PRIORITY_ORDER = { urgent: 0, high: 1, normal: 2, low: 3 };

// ══════════════════════════════════════════════
//  主组件
// ══════════════════════════════════════════════

function TicketList() {
  const navigate = useNavigate();
  const [form] = Form.useForm();
  const [searchFilters, setSearchFilters] = useState({});
  const [tickets, setTickets] = useState(MOCK_TICKETS);
  const [assignModalVisible, setAssignModalVisible] = useState(false);
  const [assignTarget, setAssignTarget] = useState(null);
  const [assignTicket, setAssignTicket] = useState(null);

  // ==================== 统计 ====================
  const stats = useMemo(() => {
    const pending = tickets.filter(t => t.status === 'pending').length;
    const processing = tickets.filter(t => t.status === 'processing').length;
    const today = new Date().toISOString().slice(0, 10);
    const todayNew = tickets.filter(t => t.createdAt.startsWith(today)).length;
    return { pending, processing, todayNew };
  }, [tickets]);

  // ==================== 筛选 ====================
  const filteredTickets = useMemo(() => {
    let list = [...tickets];
    const f = searchFilters;

    if (f.status && f.status !== 'all') {
      list = list.filter(t => t.status === f.status);
    }
    if (f.enterpriseName) {
      const kw = f.enterpriseName.toLowerCase();
      list = list.filter(t => t.enterpriseName.toLowerCase().includes(kw));
    }
    if (f.category && f.category !== 'all') {
      list = list.filter(t => t.category === f.category);
    }
    if (f.issueType) {
      list = list.filter(t => t.issueType === f.issueType);
    }
    if (f.priority && f.priority !== 'all') {
      list = list.filter(t => t.priority === f.priority);
    }
    if (f.handlerId) {
      list = list.filter(t => t.handlerId === f.handlerId);
    }
    if (f.keyword) {
      const kw = f.keyword.toLowerCase();
      list = list.filter(t =>
        t.ticketNo.toLowerCase().includes(kw) ||
        t.title.toLowerCase().includes(kw)
      );
    }

    // 默认排序：待处理优先 → 优先级高优先 → 创建时间正序
    list.sort((a, b) => {
      const sa = STATUS_ORDER[a.status] ?? 99;
      const sb = STATUS_ORDER[b.status] ?? 99;
      if (sa !== sb) return sa - sb;
      const pa = PRIORITY_ORDER[a.priority] ?? 99;
      const pb = PRIORITY_ORDER[b.priority] ?? 99;
      if (pa !== pb) return pa - pb;
      return a.createdAt.localeCompare(b.createdAt);
    });
    return list;
  }, [tickets, searchFilters]);

  // ==================== 搜索 ====================
  const handleSearch = useCallback(() => {
    const values = form.getFieldsValue();
    setSearchFilters(values);
  }, [form]);

  const handleReset = useCallback(() => {
    form.resetFields();
    setSearchFilters({});
  }, [form]);

  // ==================== 可用处理人列表（从 mock 提取） ====================
  const handlerOptions = useMemo(() => {
    const set = new Map();
    tickets.forEach(t => {
      if (t.handlerId && t.handlerName) set.set(t.handlerId, { name: t.handlerName, account: t.handlerAccount });
    });
    set.set(CURRENT_AGENT.id, { name: CURRENT_AGENT.name, account: CURRENT_AGENT.account });
    return Array.from(set, ([id, v]) => ({ value: id, label: `${v.name}  ${v.account}` }));
  }, [tickets]);

  // ==================== 行操作 ====================
  const handleClaim = useCallback((record) => {
    Modal.confirm({
      title: '确认领取该工单？',
      onOk: () => {
        setTickets(prev => prev.map(t =>
          t.key === record.key
            ? { ...t, status: 'processing', handlerId: CURRENT_AGENT.id, handlerName: CURRENT_AGENT.name, handlerAccount: CURRENT_AGENT.account, updatedAt: new Date().toISOString().replace('T', ' ').slice(0, 19) }
            : t
        ));
        message.success('已领取，工单状态更新为处理中');
      },
    });
  }, []);

  const handleAssignOpen = useCallback((record) => {
    setAssignTicket(record);
    setAssignTarget(null);
    setAssignModalVisible(true);
  }, []);

  const handleAssignConfirm = useCallback(() => {
    if (!assignTarget || !assignTicket) return;
    const target = handlerOptions.find(o => o.value === assignTarget);
    setTickets(prev => prev.map(t =>
      t.key === assignTicket.key
        ? { ...t, status: 'processing', handlerId: assignTarget, handlerName: target?.name || assignTarget, handlerAccount: target?.account || '', updatedAt: new Date().toISOString().replace('T', ' ').slice(0, 19) }
        : t
    ));
    message.success('已分配');
    setAssignModalVisible(false);
    setAssignTicket(null);
    setAssignTarget(null);
  }, [assignTarget, assignTicket, handlerOptions]);

  const handleRelease = useCallback((record) => {
    Modal.confirm({
      title: '确认释放该工单？释放后其他客服可重新领取',
      onOk: () => {
        setTickets(prev => prev.map(t =>
          t.key === record.key
            ? { ...t, status: 'pending', handlerId: null, handlerName: null, handlerAccount: null, updatedAt: new Date().toISOString().replace('T', ' ').slice(0, 19) }
            : t
        ));
        message.success('工单已释放，退回待处理池');
      },
    });
  }, []);

  const handleNavigate = useCallback((ticketNo) => {
    navigate(`/customer/ticket-manage/detail/${ticketNo}`);
  }, [navigate]);

  // ==================== 表格列 ====================
  const columns = [
    {
      title: '工单编号', dataIndex: 'ticketNo', key: 'ticketNo', width: 200, fixed: 'left',
      render: (text, record) => (
        <Button type="link" size="small" style={{ padding: 0, fontFamily: 'monospace' }}
          onClick={() => handleNavigate(record.ticketNo)}>
          {text}
        </Button>
      ),
    },
    {
      title: '标题', dataIndex: 'title', key: 'title', width: 250, ellipsis: true,
      render: (text) => <Tooltip title={text}>{text}</Tooltip>,
    },
    {
      title: '企业信息', dataIndex: 'enterpriseName', key: 'enterpriseName', width: 200,
      render: (text, record) => (
        <div>
          <div style={{ fontWeight: 500 }}>{text}</div>
          <Text type="secondary" style={{ fontSize: 12 }}>{record.enterpriseId}</Text>
        </div>
      ),
    },
    {
      title: '工单分类', dataIndex: 'category', key: 'category', width: 140,
      render: (c, record) => {
        const m = CATEGORY_MAP[c];
        return (
          <div style={{ lineHeight: 1.8 }}>
            <div>
              {m ? <Tag color={m.color} style={{ border: 'none', marginRight: 0 }}>{m.label}</Tag> : c}
            </div>
            {record.issueType && (
              <div>
                <Tag color="blue" style={{ border: 'none', marginRight: 0 }}>{record.issueType}</Tag>
              </div>
            )}
          </div>
        );
      },
    },
    {
      title: '所属App', dataIndex: 'appValue', key: 'appValue', width: 170,
      render: (v) => (v ? (APP_DISPLAY_MAP[v] || v) : <span style={{ color: '#ccc' }}>-</span>),
    },
    {
      title: '所属项目', dataIndex: 'projectName', key: 'projectName', width: 130,
      render: (v) => (v ? v : <span style={{ color: '#ccc' }}>-</span>),
    },
    {
      title: '附件', dataIndex: 'attachments', key: 'attachments', width: 90,
      render: (attachments) => {
        const list = attachments || [];
        if (list.length === 0) return <span style={{ color: '#ccc' }}>-</span>;
        return (
          <Tooltip title={list.map(f => f.name).join('、')}>
            <Space size={4} style={{ cursor: 'default' }}>
              <PaperClipOutlined style={{ color: '#1677ff' }} />
              <Text>{list.length}</Text>
            </Space>
          </Tooltip>
        );
      },
    },
    {
      title: '优先级', dataIndex: 'priority', key: 'priority', width: 90,
      render: (p) => {
        const m = PRIORITY_MAP[p];
        return m ? <Tag color={m.color} style={{ border: 'none' }}>{m.label}</Tag> : p;
      },
    },
    {
      title: '状态', dataIndex: 'status', key: 'status', width: 90,
      render: (s) => {
        const m = STATUS_MAP[s];
        return m ? <Tag color={m.color} icon={m.icon} style={{ border: 'none' }}>{m.label}</Tag> : s;
      },
    },
    {
      title: 'TAPD状态', dataIndex: 'tapd', key: 'tapd', width: 130,
      render: (tapd) => tapd ? (
        <div style={{ lineHeight: 1.8 }}>
          <Tag color="green" style={{ border: 'none', marginRight: 0 }}>已创建</Tag>
          <div>
            <Tag color={TAPD_STATUS_MAP[tapd.status]?.color || 'default'} style={{ border: 'none', marginRight: 0 }}>
              {TAPD_STATUS_MAP[tapd.status]?.label || tapd.status}
            </Tag>
          </div>
        </div>
      ) : (
        <Tag style={{ border: 'none', marginRight: 0 }}>未创建</Tag>
      ),
    },
    {
      title: '处理人', dataIndex: 'handlerName', key: 'handlerName', width: 130,
      render: (text, record) => text ? (
        <div>
          <div style={{ fontWeight: 500 }}>{text}</div>
          <Text type="secondary" style={{ fontSize: 12 }}>{record.handlerAccount}</Text>
        </div>
      ) : <span style={{ color: '#ccc' }}>-</span>,
    },
    {
      title: '创建时间', dataIndex: 'createdAt', key: 'createdAt', width: 180,
      sorter: (a, b) => a.createdAt.localeCompare(b.createdAt),
    },
    {
      title: '操作', key: 'action', fixed: 'right', width: 200,
      render: (_, record) => {
        const isPending = record.status === 'pending';
        const isProcessing = record.status === 'processing';
        return (
          <Space size={4}>
            {isPending && (
              <>
                <Button type="link" size="small" icon={<PlusOutlined />}
                  onClick={() => handleClaim(record)}>
                  领取
                </Button>
                <Button type="link" size="small"
                  onClick={() => handleAssignOpen(record)}>
                  分配
                </Button>
              </>
            )}
            {isProcessing && (
              <>
                <Button type="link" size="small"
                  onClick={() => handleNavigate(record.ticketNo)}>
                  处理
                </Button>
                <Button type="link" size="small"
                  onClick={() => handleRelease(record)}>
                  释放
                </Button>
              </>
            )}
            <Button type="link" size="small"
              onClick={() => handleNavigate(record.ticketNo)}>
              详情
            </Button>
          </Space>
        );
      },
    },
  ];


  // ==================== 渲染 ====================
  return (
    <div style={{ padding: 24 }}>
      <div style={{ marginBottom: 16 }}>
        <Title level={4}>工单管理</Title>
      </div>

      {/* 统计卡片 */}
      <Row gutter={16} style={{ marginBottom: 24 }}>
        <Col span={8}>
          <Card bordered={false}
            style={{ borderRadius: 12, boxShadow: '0 2px 8px rgba(0,0,0,0.02)' }}>
            <Statistic
              title={<span><AlertOutlined style={{ color: '#ff4d4f', marginRight: 6 }} />待处理</span>}
              value={stats.pending}
              valueStyle={{ color: '#ff4d4f', fontWeight: 'bold' }}
            />
          </Card>
        </Col>
        <Col span={8}>
          <Card bordered={false}
            style={{ borderRadius: 12, boxShadow: '0 2px 8px rgba(0,0,0,0.02)' }}>
            <Statistic
              title={<span><SyncOutlined style={{ color: '#1677ff', marginRight: 6 }} />处理中</span>}
              value={stats.processing}
              valueStyle={{ color: '#1677ff', fontWeight: 'bold' }}
            />
          </Card>
        </Col>
        <Col span={8}>
          <Card bordered={false}
            style={{ borderRadius: 12, boxShadow: '0 2px 8px rgba(0,0,0,0.02)' }}>
            <Statistic
              title="今日新增"
              value={stats.todayNew}
              valueStyle={{ fontWeight: 'bold' }}
            />
          </Card>
        </Col>
      </Row>

      {/* 搜索筛选区 */}
      <Card bordered={false}
        style={{ marginBottom: 8, borderRadius: 12, boxShadow: '0 2px 8px rgba(0,0,0,0.02)' }}>
        <Form form={form} layout="inline" initialValues={{ status: 'pending', category: 'all', priority: 'all' }}
          onFinish={handleSearch}>
          <Form.Item name="status" style={{ marginBottom: 0 }}>
            <Select style={{ width: 120 }} allowClear placeholder="状态">
              <Option value="all">全部</Option>
              <Option value="pending">待处理</Option>
              <Option value="processing">处理中</Option>
              <Option value="resolved">已解决</Option>
              <Option value="closed">已关闭</Option>
            </Select>
          </Form.Item>
          <Form.Item name="enterpriseName" style={{ marginBottom: 0 }}>
            <Input style={{ width: 200 }} placeholder="企业名称" allowClear />
          </Form.Item>
          <Form.Item name="category" style={{ marginBottom: 0 }}>
            <Select style={{ width: 120 }} allowClear placeholder="工单分类">
              <Option value="all">全部</Option>
              <Option value="techSupport">技术对接</Option>
              <Option value="bug">BUG</Option>
              <Option value="requirement">需求</Option>
              <Option value="other">其他</Option>
            </Select>
          </Form.Item>
          <Form.Item name="issueType" style={{ marginBottom: 0 }}>
            <Select style={{ width: 140 }} allowClear placeholder="问题类型">
              {ISSUE_TYPE_OPTIONS.map(t => <Option key={t} value={t}>{t}</Option>)}
            </Select>
          </Form.Item>
          <Form.Item name="priority" style={{ marginBottom: 0 }}>
            <Select style={{ width: 100 }} allowClear placeholder="优先级">
              <Option value="all">全部</Option>
              <Option value="low">低</Option>
              <Option value="normal">普通</Option>
              <Option value="high">高</Option>
              <Option value="urgent">紧急</Option>
            </Select>
          </Form.Item>
          <Form.Item name="handlerId" style={{ marginBottom: 0 }}>
            <Select style={{ width: 140 }} allowClear placeholder="处理人"
              showSearch optionFilterProp="children">
              {handlerOptions.map(opt => (
                <Option key={opt.value} value={opt.value}>{opt.label}</Option>
              ))}
            </Select>
          </Form.Item>
          <Form.Item name="keyword" style={{ marginBottom: 0 }}>
            <Input style={{ width: 200 }} placeholder="工单编号/标题" allowClear />
          </Form.Item>
          <Form.Item style={{ marginBottom: 0 }}>
            <Space>
              <Button type="primary" htmlType="submit" icon={<SearchOutlined />}>查询</Button>
              <Button icon={<ReloadOutlined />} onClick={handleReset}>重置</Button>
            </Space>
          </Form.Item>
        </Form>
      </Card>

      {/* 表格 */}
      <Card bordered={false}
        style={{ borderRadius: 12, boxShadow: '0 2px 8px rgba(0,0,0,0.02)' }}>
        <Table
          columns={columns}
          dataSource={filteredTickets}
          loading={false}
          rowKey="key"
          pagination={{
            showSizeChanger: true,
            showTotal: (total) => `共 ${total} 条记录`,
            defaultPageSize: 20,
            pageSizeOptions: ['10', '20', '50'],
          }}
          scroll={{ x: 2070 }}
          locale={{
            emptyText: (
              <div style={{ padding: 40 }}>
                <Text type="secondary">暂无工单记录</Text>
              </div>
            ),
          }}
        />
      </Card>

      {/* 分配弹窗 */}
      <Modal
        title="分配工单"
        open={assignModalVisible}
        onOk={handleAssignConfirm}
        onCancel={() => setAssignModalVisible(false)}
        okText="确认分配"
        cancelText="取消"
      >
        <div style={{ marginBottom: 12 }}>
          <Text>工单编号：</Text>
          <Text code>{assignTicket?.ticketNo}</Text>
        </div>
        <Form.Item label="分配至" style={{ marginBottom: 0 }}>
          <Select
            placeholder="请选择客服"
            value={assignTarget}
            onChange={setAssignTarget}
            showSearch
            optionFilterProp="children"
            style={{ width: '100%' }}
          >
            {MOCK_AGENTS.map(agent => (
              <Option key={agent.id} value={agent.id}>
                {agent.name}  {agent.account}
              </Option>
            ))}
          </Select>
        </Form.Item>
      </Modal>
    </div>
  );
}

export default TicketList;
